import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  generateIntegrationCredentials,
  getIntegrationCredentials,
} from "../../features/courses/courseSlice";
import { FiCopy, FiCheck, FiEye, FiEyeOff, FiKey, FiRefreshCw } from "react-icons/fi";

function CredentialField({ label, value, masked }) {
  const [hidden, setHidden] = useState(masked);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error("Error copying to clipboard:", err);
    }
  };

  const displayValue = masked && hidden ? "•".repeat(Math.min(value?.length || 24, 40)) : value;

  return (
    <div>
      <label className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">
        {label}
      </label>
      <div
        className="mt-1 flex items-center gap-2 rounded-lg px-3 py-2"
        style={{
          background: 'rgba(24, 24, 27, 0.6)',
          border: '1px solid rgba(113, 113, 122, 0.3)',
        }}
      >
        <span className="flex-1 min-w-0 truncate font-mono text-sm text-zinc-200">
          {displayValue}
        </span>
        {masked && (
          <button
            type="button"
            onClick={() => setHidden(!hidden)}
            className="text-zinc-400 hover:text-white transition-colors flex-shrink-0"
            title={hidden ? "Show" : "Hide"}
          >
            {hidden ? <FiEye className="w-4 h-4" /> : <FiEyeOff className="w-4 h-4" />}
          </button>
        )}
        <button
          type="button"
          onClick={handleCopy}
          className="text-zinc-400 hover:text-white transition-colors flex-shrink-0"
          title="Copy to clipboard"
        >
          {copied ? <FiCheck className="w-4 h-4" style={{ color: '#86b27a' }} /> : <FiCopy className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

function IntegrationCredentials() {
  const dispatch = useDispatch();
  const { integrationCredentials } = useSelector((state) => state.courses);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCredentials = async () => {
      try {
        await dispatch(getIntegrationCredentials()).unwrap();
      } catch (err) {
        console.error("Error fetching integration credentials:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCredentials();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const handleGenerate = async () => {
    if (integrationCredentials.exists) {
      const confirmed = window.confirm(
        "Regenerating will invalidate your current client_secret. Any Named Credential or External Credential already configured with it will stop working until you update them. Continue?"
      );
      if (!confirmed) return;
    }

    setIsGenerating(true);
    setError(null);
    try {
      await dispatch(generateIntegrationCredentials()).unwrap();
    } catch (err) {
      console.error("Error generating integration credentials:", err);
      setError("Failed to generate credentials. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div
      className="task-card rounded-xl overflow-hidden"
      style={{
        background: 'rgba(39, 39, 42, 0.8)',
        border: '2px solid rgba(165, 138, 194, 0.5)',
        boxShadow: '0 8px 24px rgba(165, 138, 194, 0.2)',
      }}
    >
      {/* Header */}
      <div
        className="px-4 py-3"
        style={{
          background: 'linear-gradient(135deg, rgba(165, 138, 194, 0.15), rgba(145, 118, 174, 0.1))',
        }}
      >
        <div className="flex items-center gap-2">
          <FiKey className="w-4 h-4 text-zinc-400 flex-shrink-0" />
          <h3 className="text-sm font-semibold text-white">API Credentials</h3>
        </div>
        <p className="mt-1 text-xs text-zinc-400">
          Use these to configure your org's Named Credential / External Credential.
        </p>
      </div>

      {/* Body */}
      <div className="border-t px-4 py-4 space-y-4" style={{ borderColor: 'rgba(113, 113, 122, 0.3)' }}>
        {isLoading ? (
          <p className="text-sm text-zinc-400">Loading credentials...</p>
        ) : integrationCredentials.exists ? (
          <>
            <CredentialField label="Client ID" value={integrationCredentials.clientId} masked={false} />
            <CredentialField label="Client Secret" value={integrationCredentials.clientSecret} masked />
          </>
        ) : (
          <p className="text-sm text-zinc-400">
            You haven't generated API credentials yet. Generate a client_id and client_secret to
            authenticate your org's callouts to Camp Apex.
          </p>
        )}

        {error && <p className="text-sm" style={{ color: 'rgba(252, 165, 165, 1)' }}>{error}</p>}

        <button
          onClick={handleGenerate}
          disabled={isGenerating || isLoading}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium text-sm text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            background: isGenerating
              ? 'rgba(165, 138, 194, 0.4)'
              : 'linear-gradient(135deg, rgba(165, 138, 194, 0.7), rgba(145, 118, 174, 0.7))',
            border: '1px solid rgba(165, 138, 194, 0.4)',
          }}
        >
          {integrationCredentials.exists && <FiRefreshCw className="w-4 h-4" />}
          {isGenerating
            ? "Working..."
            : integrationCredentials.exists
            ? "Regenerate"
            : "Generate"}
        </button>
      </div>
    </div>
  );
}

export default IntegrationCredentials;
